import firebase from "firebase/compat/app";
import "firebase/compat/database";
import { getApp } from "firebase/app";
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  DocumentData,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QuerySnapshot,
  startAfter,
  startAt,
  Timestamp,
  where,
} from "firebase/firestore";
import { getFirebaseConfig, getFirestoreDatabaseId } from "../firebaseConfig";
import type { IChangeRecord } from "../services/dashboard.services";

// Reads come from Firestore when this is on, otherwise from the Realtime Database
export const readFromFirestore = process.env.REACT_APP_USE_FIRESTORE === "true";

// The dashboard loads from the API and follows the user's change feed when this is on
export const changeFeedEnabled =
  readFromFirestore && process.env.REACT_APP_USE_CHANGE_FEED === "true";

let firestoreDatabaseId = getFirestoreDatabaseId("");

export const initializeFirebase = (environment: string) => {
  firestoreDatabaseId = getFirestoreDatabaseId(environment);
  const config = getFirebaseConfig(environment);
  firebase.initializeApp(config);
  console.info(
    readFromFirestore
      ? `Reading data from Firestore database ${firestoreDatabaseId}`
      : `Reading data from Realtime Database ${config.databaseURL}`
  );
};

const getRealtimeDB = () => firebase.database();
const getFirestoreDB = () => getFirestore(getApp(), firestoreDatabaseId);

export type Unsubscribe = () => void;
type ErrorHandler = (error: Error) => void;

// The same data in each database, field reads a single field of a Firestore document
export interface DataPath {
  rtdb: string;
  firestore: string;
  field?: string;
}

export interface ListQuery {
  // Firestore only, a Realtime Database query orders by one child so callers
  // filter its results themselves
  whereEqual?: { field: string; value: string };
  orderByChild?: string;
  orderByKey?: boolean;
  startAt?: string;
  startAfter?: string;
  limitToFirst?: number;
}

export const withField = (path: DataPath, field: string): DataPath => ({
  rtdb: `${path.rtdb}/${field}`,
  firestore: path.firestore,
  field,
});

const community = (communityID: string) => `communities/${communityID}`;
const unit = (communityID: string, unitID: string) =>
  `${community(communityID)}/units/${unitID}`;

// Mirrors PATH_RULES in the backend firestore_handlers.py
export const dataPaths = {
  systemStatus: (): DataPath => ({
    rtdb: "status",
    firestore: "system/status",
  }),
  unit: (communityID: string, unitID: string): DataPath => ({
    rtdb: unit(communityID, unitID),
    firestore: unit(communityID, unitID),
  }),
  unitSettings: (communityID: string, unitID: string): DataPath =>
    withField(dataPaths.unit(communityID, unitID), "settings"),
  events: (communityID: string): DataPath => ({
    rtdb: `${community(communityID)}/events`,
    firestore: `${community(communityID)}/events`,
  }),
  event: (communityID: string, eventKey: string): DataPath => ({
    rtdb: `${community(communityID)}/events/${eventKey}`,
    firestore: `${community(communityID)}/events/${eventKey}`,
  }),
  unresolvedEvents: (communityID: string): DataPath => ({
    rtdb: `${community(communityID)}/unresolved_events`,
    firestore: `${community(communityID)}/unresolved_events`,
  }),
  unreadSoftEvents: (communityID: string, userID: string): DataPath => ({
    rtdb: `${community(communityID)}/unread_events_soft/${userID}`,
    firestore: `${community(communityID)}/unread_events_soft/${userID}/events`,
  }),
  eventNotes: (communityID: string, eventKey: string): DataPath => ({
    rtdb: `${community(communityID)}/event_notes/${eventKey}`,
    firestore: `${community(communityID)}/event_notes/${eventKey}/notes`,
  }),
  report: (
    communityID: string,
    unitID: string,
    type: string,
    date: string
  ): DataPath => ({
    rtdb: `${unit(communityID, unitID)}/reports/${type}/${date}`,
    firestore: `${unit(communityID, unitID)}/reports/${type}/dates/${date}`,
  }),
  reportRecommendation: (
    communityID: string,
    unitID: string,
    type: string,
    date: string
  ): DataPath => ({
    rtdb: `${unit(communityID, unitID)}/report_recommendations/${type}/${date}`,
    firestore: `${unit(
      communityID,
      unitID
    )}/report_recommendations/${type}/dates/${date}`,
  }),
};

// Key used for events, unresolved events and event notes in both databases
export const eventKey = (timeCreated: string, eventID: string) =>
  `${timeCreated.replace("T", " ")}-${eventID}`;

const rtdbQuery = (path: string, listQuery?: ListQuery) => {
  let ref: firebase.database.Query = getRealtimeDB().ref(path);
  if (listQuery?.orderByChild) ref = ref.orderByChild(listQuery.orderByChild);
  if (listQuery?.orderByKey) ref = ref.orderByKey();
  if (listQuery?.startAt !== undefined) ref = ref.startAt(listQuery.startAt);
  if (listQuery?.startAfter !== undefined)
    ref = ref.startAfter(listQuery.startAfter);
  if (listQuery?.limitToFirst !== undefined)
    ref = ref.limitToFirst(listQuery.limitToFirst);
  return ref;
};

const firestoreQuery = (path: string, listQuery?: ListQuery) => {
  const constraints: QueryConstraint[] = [];
  if (listQuery?.whereEqual)
    constraints.push(
      where(listQuery.whereEqual.field, "==", listQuery.whereEqual.value)
    );
  if (listQuery?.orderByChild)
    constraints.push(orderBy(listQuery.orderByChild));
  if (listQuery?.orderByKey) constraints.push(orderBy(documentId()));
  if (listQuery?.startAt !== undefined)
    constraints.push(startAt(listQuery.startAt));
  if (listQuery?.startAfter !== undefined)
    constraints.push(startAfter(listQuery.startAfter));
  if (listQuery?.limitToFirst !== undefined)
    constraints.push(limit(listQuery.limitToFirst));
  return query(collection(getFirestoreDB(), path), ...constraints);
};

const documentValue = (data: DocumentData | undefined, field?: string) => {
  const value = field ? data?.[field] : data;
  return value ?? null;
};

// Matches a Realtime Database value, null when there are no items
const listValue = (snapshot: QuerySnapshot) => {
  if (snapshot.empty) return null;
  const values: Record<string, any> = {};
  snapshot.docs.forEach((item) => {
    values[item.id] = item.data();
  });
  return values;
};

// Development only tally of documents received by path, roughly what Firestore
// bills as reads. Run firestoreReads in the browser console to see it
const readCounts: Record<string, number> = {};
const countReads = (path: string, count: number) => {
  if (process.env.NODE_ENV === "production") return;
  // Ids are replaced so every unit or event adds to the same row
  const key = path
    .split("/")
    .map((segment, index) => (index % 2 === 1 ? "{id}" : segment))
    .join("/");
  readCounts[key] = (readCounts[key] ?? 0) + count;
};
if (process.env.NODE_ENV !== "production") {
  (window as any).firestoreReads = () => {
    const rows = Object.entries(readCounts)
      .sort(([, a], [, b]) => b - a)
      .map(([path, reads]) => ({ path, reads }));
    console.table(rows);
    return rows.reduce((total, row) => total + row.reads, 0);
  };
}

// A query listener bills each document it receives, and one read when it finds nothing
const queryReadCounter = (path: string) => {
  let first = true;
  return (snapshot: QuerySnapshot) => {
    countReads(path, snapshot.docChanges().length || (first ? 1 : 0));
    first = false;
  };
};

// Calls onValue with the current value and again on every change
export const listenToDocument = (
  path: DataPath,
  onValue: (value: any) => void,
  onError?: ErrorHandler
): Unsubscribe => {
  if (readFromFirestore) {
    return onSnapshot(
      doc(getFirestoreDB(), path.firestore),
      (snapshot) => {
        countReads(path.firestore, 1);
        onValue(documentValue(snapshot.data(), path.field));
      },
      onError
    );
  }
  const ref = getRealtimeDB().ref(path.rtdb);
  const listener = ref.on(
    "value",
    (snapshot) => onValue(snapshot.val()),
    onError
  );
  return () => ref.off("value", listener);
};

// Calls onValue with every item keyed by id, and again on every change
export const listenToList = (
  path: DataPath,
  onValue: (values: Record<string, any> | null) => void,
  options?: { query?: ListQuery; onError?: ErrorHandler }
): Unsubscribe => {
  if (readFromFirestore) {
    const countQueryReads = queryReadCounter(path.firestore);
    return onSnapshot(
      firestoreQuery(path.firestore, options?.query),
      (snapshot) => {
        countQueryReads(snapshot);
        onValue(listValue(snapshot));
      },
      options?.onError
    );
  }
  const ref = rtdbQuery(path.rtdb, options?.query);
  const listener = ref.on(
    "value",
    (snapshot) => onValue(snapshot.val()),
    options?.onError
  );
  return () => ref.off("value", listener);
};

// onAdded runs for every existing item first, then for each new one
export const listenToListChanges = (
  path: DataPath,
  handlers: {
    onAdded?: (value: any) => void;
    onRemoved?: (value: any) => void;
    onError?: ErrorHandler;
  },
  listQuery?: ListQuery
): Unsubscribe => {
  if (readFromFirestore) {
    const countQueryReads = queryReadCounter(path.firestore);
    return onSnapshot(
      firestoreQuery(path.firestore, listQuery),
      (snapshot) => {
        countQueryReads(snapshot);
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") handlers.onAdded?.(change.doc.data());
          if (change.type === "removed")
            handlers.onRemoved?.(change.doc.data());
        });
      },
      handlers.onError
    );
  }
  const ref = rtdbQuery(path.rtdb, listQuery);
  const added = handlers.onAdded
    ? ref.on(
        "child_added",
        (snapshot) => handlers.onAdded?.(snapshot.val()),
        handlers.onError
      )
    : undefined;
  const removed = handlers.onRemoved
    ? ref.on(
        "child_removed",
        (snapshot) => handlers.onRemoved?.(snapshot.val()),
        handlers.onError
      )
    : undefined;
  return () => {
    if (added) ref.off("child_added", added);
    if (removed) ref.off("child_removed", removed);
  };
};

// Calls onValue with the community tags grouped by unit id, then by tag id.
// Users limited to some units pass unitIDs, since the rules only let them
// read the tags of their own units
export const listenToRFIDTags = (
  communityID: string,
  onValue: (tags: Record<string, Record<string, any>> | null) => void,
  onError?: ErrorHandler,
  unitIDs?: string[]
): Unsubscribe => {
  if (readFromFirestore && unitIDs) {
    const tags: Record<string, Record<string, any>> = {};
    const unsubscribes = unitIDs.map((unitID) => {
      const tagsPath = `${community(communityID)}/rfid_tags/${unitID}/tags`;
      const countQueryReads = queryReadCounter(tagsPath);
      return onSnapshot(
        collection(getFirestoreDB(), tagsPath),
        (snapshot) => {
          countQueryReads(snapshot);
          const unitTags = listValue(snapshot);
          if (unitTags) tags[unitID] = unitTags;
          else delete tags[unitID];
          onValue(Object.keys(tags).length > 0 ? { ...tags } : null);
        },
        onError
      );
    });
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }
  if (readFromFirestore) {
    const tagsQuery = query(
      collectionGroup(getFirestoreDB(), "tags"),
      where("community_id", "==", communityID)
    );
    const countQueryReads = queryReadCounter("tags collection group");
    return onSnapshot(
      tagsQuery,
      (snapshot) => {
        countQueryReads(snapshot);
        if (snapshot.empty) return onValue(null);
        const tags: Record<string, Record<string, any>> = {};
        snapshot.docs.forEach((tag) => {
          const unitID = tag.data().unit_id;
          tags[unitID] = { ...tags[unitID], [tag.id]: tag.data() };
        });
        onValue(tags);
      },
      onError
    );
  }
  const ref = getRealtimeDB().ref(`${community(communityID)}/rfid_tags`);
  const listener = ref.on(
    "value",
    (snapshot) => onValue(snapshot.val()),
    onError
  );
  return () => ref.off("value", listener);
};

// Starts this far before the as-of, covering clock differences and write delay
const CHANGE_FEED_OVERLAP_MS = 60 * 1000;
const CHANGE_FEED_REANCHOR_MS = 60 * 60 * 1000;

// Calls onRecords with each new change record for the user in time order,
// initial is true for the first snapshot. Records seen once are not sent again
export const listenToChangeFeed = (
  userID: string,
  since: string,
  onRecords: (records: IChangeRecord[], initial: boolean) => void,
  onError: ErrorHandler
): Unsubscribe => {
  const path = `users/${userID}/changes`;
  const seen = new Set<string>();
  let newestTime: Timestamp | null = null;
  let anchorTime: Timestamp | null = null;
  let initial = true;

  const listen = (start: Timestamp, onFirstSnapshot?: () => void) => {
    const countQueryReads = queryReadCounter(path);
    let first = true;
    return onSnapshot(
      query(
        collection(getFirestoreDB(), path),
        where("time", ">=", start),
        orderBy("time")
      ),
      (snapshot) => {
        countQueryReads(snapshot);
        // Records are never edited, a removed change is the TTL deleting an old one
        const records: IChangeRecord[] = [];
        snapshot.docChanges().forEach((change) => {
          if (change.type !== "added" || seen.has(change.doc.id)) return;
          seen.add(change.doc.id);
          const { time, expire_at, ...record } = change.doc.data();
          if (newestTime === null || time.toMillis() > newestTime.toMillis())
            newestTime = time;
          records.push(record as IChangeRecord);
        });
        if (records.length > 0) onRecords(records, initial);
        initial = false;
        if (first) {
          first = false;
          onFirstSnapshot?.();
        }
      },
      onError
    );
  };

  let unsubscribe = listen(
    Timestamp.fromMillis(Date.parse(since) - CHANGE_FEED_OVERLAP_MS)
  );
  let previous: Unsubscribe | null = null;

  // The query's lower bound stays put, so a long open page moves it forward when records arrived
  const interval = setInterval(() => {
    if (newestTime === null || newestTime === anchorTime || previous) return;
    anchorTime = newestTime;
    previous = unsubscribe;
    unsubscribe = listen(
      Timestamp.fromMillis(anchorTime.toMillis() - CHANGE_FEED_OVERLAP_MS),
      () => {
        previous?.();
        previous = null;
      }
    );
  }, CHANGE_FEED_REANCHOR_MS);

  return () => {
    clearInterval(interval);
    previous?.();
    unsubscribe();
  };
};
